'use client';

import { forwardRef } from 'react';

import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import Image from 'next/image';

import { ServiceTheme } from '@/config/services.config';
import colors from '@/styles/themes/colors';
import { MainFeature } from '@/types/feature.type';

import styles from './ServiceFeatureCard.module.scss';

interface ServiceFeatureCardProps extends MainFeature {
  reverse?: boolean;
  theme: ServiceTheme;
  visible?: boolean;
}

const ServiceFeatureCard = forwardRef<HTMLDivElement, ServiceFeatureCardProps>(
  ({ title, description, image, techStack, bulletColor = 'blue', reverse, theme, visible }, ref) => (
    <Grid
      ref={ref}
      container
      spacing={{ xs: 5, md: 4 }}
      sx={{ flexDirection: { xs: 'column', md: 'row' } }}
      className={cx(styles.container, { [styles.reverse]: reverse, [styles.visible]: visible })}
    >
      <Grid container size={{ xs: 12, md: 6 }}>
        <Box className={cx(styles.boxWrapper, styles[bulletColor])}>
          <Image
            src={image.src}
            alt={image.alt}
            fill
            className={styles.image}
            sizes="(min-width: 768px) 560px, 100vw"
          />
        </Box>
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }}>
        <Stack gap={{ xs: 2, md: 4 }} className={styles.content}>
          {techStack && techStack.length > 0 && (
            <Stack direction="row" className={styles.chips}>
              {techStack.map((tech, index) => (
                <Chip
                  key={`${tech}-${index + 1}`}
                  variant="filled"
                  label={tech}
                  className={cx(styles.chip, styles[`chip${theme.charAt(0).toUpperCase() + theme.slice(1)}`])}
                />
              ))}
            </Stack>
          )}
          <Typography
            component="h2"
            variant="h5"
            sx={{
              typography: {
                md: 'h3',
              },
            }}
            color={colors.white}
          >
            {title}
          </Typography>
          <Typography variant="body1" className={styles.description} whiteSpace="pre-line">
            {description}
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  )
);

ServiceFeatureCard.displayName = 'ServiceFeatureCard';

export default ServiceFeatureCard;
